import * as React from 'react';
import UserSession from '@app/common/user-session';
import ExclamationCircleIcon from '@patternfly/react-icons/dist/js/icons/exclamation-circle-icon';
import { Saver } from '@app/components/Saver';
import { PasswordPolicy } from '@app/components/PasswordPolicy';
import { Spacer } from '@app/components/Spacer';
import { textInputValidate, textInputRequiredOnly, validatePassword } from '@app/lib/validator';
import { Button } from '@patternfly/react-core';
import { AuthenticationDetails, CognitoUserPool, CognitoUser } from "amazon-cognito-identity-js";
import '../../app.css';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Form, FormGroup, Label, Input, Col, Row } from 'reactstrap';
import VideoBackground from '@app/components/VideoBackground';
import { connect } from 'react-redux';

interface NewPasswordChallengeProps extends RouteComponentProps {
  dispatch?: any;
}

interface NewPasswordChallengeState {
  newPassword: string;
  confirmPassword: string;
  newPasswordSet: boolean;
  message: string;
  isLoading?: boolean;
  oldPassword?: string;
}

class NewPasswordChallenge extends React.Component<NewPasswordChallengeProps, NewPasswordChallengeState> {
  constructor(props: NewPasswordChallengeProps) {
    super(props);
    this.state = {
      newPassword: "",
      confirmPassword: "",
      newPasswordSet: false,
      message: "Your temporary password must be changed. Please set a new password",
      isLoading: false,
      oldPassword: ""
    };
  }

  handleChange = (key: keyof NewPasswordChallengeState, value: any) => {
    this.setState((prevState: NewPasswordChallengeState) => ({
      ...prevState,
      [key]: value
    }));    
  };

  handleLoginClick = () => {
    this.props.history.push('/');
  }

  handleSubmit = async () => {
    const { newPassword, confirmPassword } = this.state;
    const passwordValidator = validatePassword(newPassword, confirmPassword);

    if (passwordValidator.isValid === true) {
      this.setState(() => ({ 
        isLoading: true
      }));
      await this.setPassword(newPassword);
    }
    else {
      this.setState(() => ({ 
        message: passwordValidator.error || "",
        isLoading: false 
      }));
    }
  };

  setPassword = async (newPassword) => {
    const tempPass = UserSession.getParam("tempPass");
    const userName = UserSession.getParam("userName");
    const poolId = process.env.REACT_APP_USER_POOL_ID || "";
    const clientId = process.env.REACT_APP_POOL_WEB_CLI_ID || "";
    const pool = new CognitoUserPool({ UserPoolId: poolId, ClientId: clientId });
    const authDetails = new AuthenticationDetails({ Username: userName, Password: tempPass });
    const cognitoUser = new CognitoUser({ Username: userName, Pool: pool });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: async (result) => {},
      newPasswordRequired: async (userAttributes, requiredAttributes) => {
        let result = await cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
          onSuccess: (result) => {
            UserSession.clear();
            this.setState(() => ({ 
              message: "Your new password was set successfully! You can login now with your new password",
              newPasswordSet: true,
              isLoading: false
            }));
          },
          onFailure: (error) => {
            console.log(error);
            this.setState(() => ({ 
              message: "There was an error setting your new password",
              isLoading: false
            }));
          }
        });
      },
      onFailure: async (error) => {
        console.log(error);
        const { newPassword, confirmPassword, newPasswordSet, message } = this.state;
        this.setState(() => ({
          showHelperText: true,
          isLoading: false,
          newPassword,
          confirmPassword,
          newPasswordSet,
          message,
        }));
      }
    });
  }

  render() {
    const { newPassword, confirmPassword, newPasswordSet, message } = this.state;
    const passwordErrorState = newPassword 
      ? textInputRequiredOnly(newPassword) 
      : { validated: 'default' as 'default', errorText: "" };

    const confirmPasswordErrorState = confirmPassword 
      ? textInputValidate(confirmPassword, true, newPassword === confirmPassword, "Passwords must match") 
      : { validated: 'default' as 'default', errorText: "" };
    const formValid = newPassword && confirmPassword && (newPassword === confirmPassword);

    return (
      <React.Fragment>
        <Helmet>
          <title>Create Password</title>
        </Helmet>
        <Row className="h-100 w-100 login-page overflow-x-hidden">
          <Col lg={8} md={8} sm={12} xs={12} className="bg-white">
            <div>
              <VideoBackground/>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12} xs={12} className="bg-white align-content-center">
            <Row>
              <Col lg={8} md={8} sm={8} xs={8} className="m-auto">
                <div className="login-form pb-3">
                  <h2 className="text-center mb-4">Create Password</h2>

                  {!newPasswordSet ? 
                    <div>
                      <p className="text-center mb-4">{message}</p>

                      <Form>
                        <FormGroup className="mb-3">
                          <Label for="newPassword">New Password</Label>
                          <Input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => this.handleChange("newPassword", e.target.value)}
                            autoComplete='new-password'
                          />
                        </FormGroup>

                        <FormGroup className="mb-3">
                          <Label for="confirmPassword">Confirm Password</Label>
                          <Input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => this.handleChange("confirmPassword", e.target.value)}
                            autoComplete='new-password'
                          />
                        </FormGroup>

                        <div className="mb-4">
                          <PasswordPolicy />
                        </div>
                      </Form>
                    </div>
                  : 
                    <div>
                      <p className="text-center mb-4">{message}</p>
                      <div className="d-flex justify-content-center">
                        <Button 
                          color="secondary" 
                          className="ethica-button-green w-auto" 
                          isBlock
                          onClick={this.handleLoginClick}
                        >
                          Login Now
                        </Button>
                      </div>
                    </div>
                  }

                  {!newPasswordSet ? 
                    <div className="d-flex justify-content-center w-100 py-4">                        
                      <Saver 
                        submitButtonText="Submit" 
                        submitButtonisDisabled={!formValid}
                        submit={async () => this.handleSubmit()}
                        showCancel={false}
                        showConfirmationMessage={false}
                        isLoading={this.state.isLoading}
                        message={message}
                        confirmationMessage=""
                      />
                    </div>
                  : <></> }
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </React.Fragment>
    );
  }
}

export default connect()(withRouter(NewPasswordChallenge));